import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { Tenant } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { TenantStatus } from '@sunstone/api-client/gen/tenants/v1/tenants_pb';
import { useListTenants } from '@sunstone/api-client/hooks';
import { Button, DataTable, PillFilter } from '@sunstone/ui';
import { formatDateTime } from '@sunstone/utils';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { type Blip, BlipsStrip } from '@/features/health/HealthBlips';
import { useDebounce } from '@/hooks/useDebounce';
import { useListState } from '@/hooks/useListState';

const PAGE_SIZE = 100;

function tenantStatusKey(status: TenantStatus): string {
  switch (status) {
    case TenantStatus.ACTIVE:
      return 'TENANT_STATUS_ACTIVE';
    case TenantStatus.INACTIVE:
      return 'TENANT_STATUS_INACTIVE';
    default:
      return 'TENANT_STATUS_UNSPECIFIED';
  }
}

function tenantStatusFromKey(key: string | null): TenantStatus {
  switch (key) {
    case 'TENANT_STATUS_ACTIVE':
      return TenantStatus.ACTIVE;
    case 'TENANT_STATUS_INACTIVE':
      return TenantStatus.INACTIVE;
    default:
      return TenantStatus.UNSPECIFIED;
  }
}

type TenantRow = Tenant & { _type: 'partner' | 'customer' };

interface TenantListProps {
  mode: 'partners' | 'customers';
  parentId?: string;
  urlParams?: boolean;
  healthBlipsByEntityId?: Record<string, Blip[]>;
  healthLoading?: boolean;
}

export function TenantList({
  mode,
  parentId,
  urlParams = false,
  healthBlipsByEntityId,
  healthLoading,
}: TenantListProps) {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { isPlatformAdmin } = useAuthorization();

  const { page, search, setPage, setSearch, getFilter, setFilter } = useListState(urlParams);
  const statusFilter = getFilter('status');

  const debouncedSearch = useDebounce(search);

  const handleStatusFilterChange = useCallback(
    (v: string | null) => setFilter('status', v),
    [setFilter],
  );

  const handleSearchChange = useCallback((v: string) => setSearch(v), [setSearch]);

  const { data, isLoading, error } = useListTenants(token, parentId, {
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
    typeFilter: mode === 'partners' ? 'partner' : 'customer',
    statusFilter: tenantStatusFromKey(statusFilter),
  });

  const allTenants = data?.tenants ?? [];
  const totalCount = data?.totalCount ?? 0;

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        Failed to load {mode === 'partners' ? 'partners' : 'customers'}.
      </div>
    );
  }

  const rows: TenantRow[] = allTenants.map((t) => ({
    ...t,
    _type: mode === 'partners' ? 'partner' : 'customer',
  }));

  const partnerColumns: ColumnDef<TenantRow>[] = [
    {
      id: 'displayName',
      accessorKey: 'displayName',
      header: 'Name',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left w-full cursor-pointer"
          onClick={() => navigate(`/tenants/${row.original.id}`)}
        >
          <span className="font-medium text-foreground hover:text-primary transition-colors">
            {row.original.displayName}
          </span>
        </button>
      ),
    },
    {
      id: 'slug',
      accessorKey: 'slug',
      header: 'URL Name',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground font-mono">{row.original.slug}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={tenantStatusKey(row.original.status)} />,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground hidden md:block">
          {row.original.createdAt ? formatDateTime(timestampDate(row.original.createdAt)) : '—'}
        </span>
      ),
    },
    ...(healthBlipsByEntityId || healthLoading
      ? [
          {
            id: 'health',
            header: 'Health (24h)',
            cell: ({ row }: { row: { original: TenantRow } }) => (
              <div className="w-40">
                <BlipsStrip
                  blips={healthBlipsByEntityId?.[row.original.id] ?? []}
                  isLoading={healthLoading}
                />
              </div>
            ),
          } as ColumnDef<TenantRow>,
        ]
      : []),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground/40 hover:text-muted-foreground"
          onClick={() => navigate(`/tenants/${row.original.id}`)}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  const customerColumns: ColumnDef<TenantRow>[] = [
    {
      id: 'displayName',
      accessorKey: 'displayName',
      header: 'Name',
      cell: ({ row }) => (
        <button
          type="button"
          className="text-left w-full cursor-pointer"
          onClick={() => navigate(`/tenants/${row.original.id}`)}
        >
          <span className="font-medium text-foreground hover:text-primary transition-colors">
            {row.original.displayName}
          </span>
        </button>
      ),
    },
    ...(!parentId && isPlatformAdmin
      ? [
          {
            id: 'partner',
            header: 'Partner',
            cell: ({ row }: { row: { original: TenantRow } }) => (
              <span className="text-sm text-muted-foreground">
                {row.original.parentName || '—'}
              </span>
            ),
          } as ColumnDef<TenantRow>,
        ]
      : []),
    {
      id: 'slug',
      accessorKey: 'slug',
      header: 'URL Name',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground font-mono">{row.original.slug}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={tenantStatusKey(row.original.status)} />,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground hidden md:block">
          {row.original.createdAt ? formatDateTime(timestampDate(row.original.createdAt)) : '—'}
        </span>
      ),
    },
    ...(healthBlipsByEntityId || healthLoading
      ? [
          {
            id: 'health',
            header: 'Health (24h)',
            cell: ({ row }: { row: { original: TenantRow } }) => (
              <div className="w-40">
                <BlipsStrip
                  blips={healthBlipsByEntityId?.[row.original.id] ?? []}
                  isLoading={healthLoading}
                />
              </div>
            ),
          } as ColumnDef<TenantRow>,
        ]
      : []),
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground/40 hover:text-muted-foreground"
          onClick={() => navigate(`/tenants/${row.original.id}`)}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  const columns = mode === 'partners' ? partnerColumns : customerColumns;

  const filterSlot = (
    <PillFilter
      label="Status"
      value={statusFilter}
      onChange={handleStatusFilterChange}
      options={[
        { value: 'TENANT_STATUS_ACTIVE', label: 'Active' },
        { value: 'TENANT_STATUS_INACTIVE', label: 'Inactive' },
      ]}
    />
  );

  return (
    <DataTable
      columns={columns}
      data={rows}
      showSearch
      searchPlaceholder="Search"
      searchValue={search}
      onSearchChange={handleSearchChange}
      filterSlot={filterSlot}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={page}
      onPageChange={setPage}
      pageSize={PAGE_SIZE}
      onRowClick={(row) => navigate(`/tenants/${row.id}`)}
    />
  );
}
